import html2canvas from "html2canvas";
import jsPDF from "jspdf";
import { saveAs } from 'file-saver';
import * as XLSX from 'xlsx';

// Helper function to clean status text for PDF (remove special characters)
const cleanStatusText = (status) => {
  if (!status) return 'Normal';
  
  let cleaned = status
    .replace(/⚠️/g, '')
    .replace(/✓/g, '')
    .replace(/[^\x20-\x7E]/g, '')
    .trim();
  
  if (!cleaned) return 'Normal';
  
  cleaned = cleaned
    .replace(/Below\s+(\d+)/g, 'Below $1°C')
    .replace(/Above\s+(\d+)/g, 'Above $1°C')
    .replace(/Normal/g, 'Normal');
  
  return cleaned;
};

export const generateTimelinePDF = async (elementId, options = {}) => {
  const {
    filename = "Temperature_Timeline_Report.pdf",
    title = "Temperature Timeline Report",
    orientation = "landscape",
    organization = "",
    season = "",
    typeOfDryer = "",
    binNo = "",
    cycleNo = "",
    warehouseName = "",
    minNormalTemp = 38,
    maxNormalTemp = 42,
    unit = "mm",
    format = "a4",
  } = options;

  try {
    let element = document.getElementById(elementId);
    if (!element) {
      element = document.querySelector(`#${elementId}`);
    }
    if (!element) {
      throw new Error("Element not found");
    }

    const loadingOverlay = document.createElement("div");
    loadingOverlay.id = "pdf-loading-overlay";
    loadingOverlay.style.cssText = `
      position: fixed;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
      background: rgba(0,0,0,0.7);
      display: flex;
      justify-content: center;
      align-items: center;
      z-index: 9999;
      color: white;
      font-size: 24px;
      font-family: Arial, sans-serif;
      z-index: 10000;
    `;
    loadingOverlay.innerHTML = `
      <div style="text-align: center;">
        <div>📄 Generating PDF Report...</div>
        <div style="font-size: 14px; margin-top: 10px;">Please wait</div>
      </div>
    `;
    document.body.appendChild(loadingOverlay);

    const filterChips = Array.from(element.querySelectorAll('.MuiChip-root')).map(chip => chip.textContent);
    const statisticsCards = Array.from(element.querySelectorAll('.MuiCard-root'));
    
    let chartSection = element.querySelector('#chart-only-paper');
    if (!chartSection) {
      chartSection = element.querySelector('.recharts-responsive-container')?.closest('.MuiPaper-root');
    }
    if (!chartSection) {
      chartSection = element.querySelector('.recharts-wrapper')?.closest('.MuiPaper-root');
    }
    
    let tableRows = [];
    const tableBody = element.querySelector('.MuiTableBody-root');
    if (tableBody) {
      tableRows = Array.from(tableBody.querySelectorAll('.MuiTableRow-root'));
    } else {
      const anyTableBody = element.querySelector('table tbody');
      if (anyTableBody) {
        tableRows = Array.from(anyTableBody.querySelectorAll('tr'));
      }
    }
    
    const totalRecords = tableRows.length;
    console.log("Total records found in DOM:", totalRecords);
    
    const pdf = new jsPDF({
      orientation: orientation,
      unit: unit,
      format: format,
    });
    
    const pageWidth = pdf.internal.pageSize.getWidth();
    const pageHeight = pdf.internal.pageSize.getHeight();
    
    let currentPage = 1;
    let yPos = 18;
  
    const addPageHeader = (yPos) => {
      pdf.setFontSize(20);
      pdf.setTextColor(26, 35, 126);
      pdf.setFont("helvetica", "bold");
      pdf.text("Temperature Timeline Report", pageWidth / 2, yPos, { align: "center" });
      yPos += 10;
      
      const filters = [];
      if (organization && organization.trim()) filters.push(`Organization: ${organization}`);
      if (warehouseName && warehouseName.trim()) filters.push(`Warehouse: ${warehouseName}`);
      if (season && season.trim()) filters.push(`Season: ${season}`);
      if (typeOfDryer && typeOfDryer.trim()) filters.push(`Dryer Type: ${typeOfDryer}`);
      if (binNo && binNo.trim()) filters.push(`Bin No: ${binNo}`);
      if (cycleNo && cycleNo.trim()) filters.push(`Cycle No: ${cycleNo}`);
      
      if (filters.length > 0) {
        pdf.setFontSize(9);
        pdf.setTextColor(80, 80, 80);
        pdf.setFont("helvetica", "normal");
        const filterText = filters.join("  |  ");
        pdf.text(filterText, pageWidth / 2, yPos, { align: "center" });
        yPos += 7;
      }
      
      const periodChip = filterChips.find(chip => chip && chip.includes('Period:'));
      if (periodChip) {
        pdf.setFontSize(10);
        pdf.setTextColor(100, 100, 100);
        pdf.setFont("helvetica", "normal");
        pdf.text(periodChip, pageWidth / 2, yPos, { align: "center" });
        yPos += 7;
      }
      
      pdf.setFontSize(9);
      pdf.setTextColor(120, 120, 120);
  
      yPos += 6;
      pdf.text(`Generated: ${new Date().toLocaleString()}`, pageWidth / 2, yPos, { align: "center" });
      yPos += 10;
      
      return yPos;
    };
   
    const addTableHeader = (yPos) => {
      const headers = [];
      const colWidths = [12, 70, 35, 40];

    
      pdf.setTextColor(255, 255, 255);
      pdf.setFontSize(8);
      pdf.setFont("helvetica", "bold");

      let xPos = 15;

      headers.forEach((header, i) => {
        pdf.rect(xPos, yPos - 4, colWidths[i], 7, "F");
        pdf.setDrawColor(0, 0, 0);
        pdf.setLineWidth(0.3);
        pdf.rect(xPos, yPos - 4, colWidths[i], 7);
        pdf.text(header, xPos + 2, yPos);
        xPos += colWidths[i];
      });

      pdf.setTextColor(0, 0, 0);
      pdf.setFont("helvetica", "normal");

      return yPos + 4;
    };
    
    const addFooter = (yPos) => {
      const footerY = pageHeight - 15;
      
      pdf.setFontSize(7);
      pdf.setTextColor(120, 120, 120);
      pdf.setFont("helvetica", "italic");
      pdf.text(`Normal Range: ${minNormalTemp}°C - ${maxNormalTemp}°C`, 15, footerY);
      pdf.text(`Total Records: ${totalRecords}`, pageWidth / 2, footerY, { align: "center" });
      pdf.text(`Page ${currentPage}`, pageWidth / 2, yPos, { align: "center" });
      pdf.text(`Page ${currentPage} of ${pdf.getNumberOfPages()}`, pageWidth - 50, footerY);
      pdf.text(`Temperature Monitoring System`, pageWidth - 50, footerY + 4);
    };
    
    const addNewPage = () => {
      pdf.addPage();
      currentPage++;
      yPos = 18;
      yPos = addPageHeader(yPos);
      return yPos;
    };
    
    let chartImageData = null;
    if (chartSection) {
      try {
        console.log("Capturing chart...");
        
        const originalBorder = chartSection.style.border;
        const originalBorderRadius = chartSection.style.borderRadius;
        const originalBoxShadow = chartSection.style.boxShadow;
        const originalPadding = chartSection.style.padding;
        const originalMargin = chartSection.style.margin;
        const originalWidth = chartSection.style.width;
        const originalMaxWidth = chartSection.style.maxWidth;
        const originalOverflow = chartSection.style.overflow;
        
        chartSection.style.borderRadius = '12px';
        chartSection.style.padding = '15px';
        chartSection.style.margin = '10px 0';
        chartSection.style.backgroundColor = '#ffffff';
        chartSection.style.width = "1400px";
        chartSection.style.maxWidth = "1400px";
        chartSection.style.overflow = "visible";
        
        const canvas = await html2canvas(chartSection, {
          scale: 4,
          backgroundColor: "#ffffff",
          logging: false,
          useCORS: true,
          allowTaint: false,
          windowWidth: 1600,
          windowHeight: chartSection.scrollHeight + 200,
          scrollX: 0,
          scrollY: 0,
        });
        
        chartImageData = canvas.toDataURL("image/png");
        
        chartSection.style.border = originalBorder;
        chartSection.style.borderRadius = originalBorderRadius;
        chartSection.style.boxShadow = originalBoxShadow;
        chartSection.style.padding = originalPadding;
        chartSection.style.margin = originalMargin;
        chartSection.style.width = originalWidth;
        chartSection.style.maxWidth = originalMaxWidth;
        chartSection.style.overflow = originalOverflow;
        
        console.log("Chart captured successfully");
      } catch (err) {
        console.error("Error capturing chart:", err);
      }
    }
    
    yPos = addPageHeader(yPos);
  
    if (statisticsCards.length > 0 && yPos < pageHeight - 80) {
      const cardsY = yPos;
      const cardWidth = (pageWidth - 40) / Math.min(5, statisticsCards.length);
      
      statisticsCards.slice(0, 5).forEach((card, idx) => {
        const cardX = 15 + (idx * cardWidth);
        
        let valueElem = card.querySelector('.MuiTypography-h5');
        if (!valueElem) {
          valueElem = card.querySelector('[class*="MuiTypography"]');
        }
        
        let labelElem = card.querySelector('.MuiTypography-caption');
        if (!labelElem) {
          labelElem = card.querySelector('[class*="MuiTypography"]:first-child');
        }
        
        const value = valueElem?.textContent || '0';
        const label = labelElem?.textContent || '';
        
        pdf.setFillColor(248, 249, 250);
        pdf.rect(cardX, cardsY, cardWidth - 2, 25, "F");
        pdf.setDrawColor(200, 200, 200);
        pdf.rect(cardX, cardsY, cardWidth - 2, 25);
        
        pdf.setFontSize(7);
        pdf.setTextColor(100, 100, 100);
        pdf.setFont("helvetica", "bold");
        pdf.text(label, cardX + 3, cardsY + 6);
        
        pdf.setFontSize(14);
        pdf.setTextColor(33, 33, 33);
        pdf.setFont("helvetica", "bold");
        pdf.text(value, cardX + 3, cardsY + 18);
      });
      yPos = cardsY + 32;
    }
    
    if (chartImageData && yPos < pageHeight - 100) {
      try {
        const imgProps = pdf.getImageProperties(chartImageData);
        const margin = 10;
        const maxWidth = pageWidth - (margin * 2);
        const aspectRatio = imgProps.width / imgProps.height;
        
        let chartWidth = maxWidth;
        let chartHeight = chartWidth / aspectRatio;
        const maxHeight = 95;
        
        if (chartHeight > maxHeight) {
          chartHeight = maxHeight;
          chartWidth = chartHeight * aspectRatio;
        }
        
        const chartX = (pageWidth - chartWidth) / 2;
        
        pdf.setFillColor(255, 255, 255);
        pdf.rect(chartX - 2, yPos - 2, chartWidth + 4, chartHeight + 4, "F");
        pdf.setDrawColor(26, 35, 126);
        pdf.setLineWidth(0.6);
        pdf.rect(chartX - 1, yPos - 1, chartWidth + 2, chartHeight + 2);
        
        pdf.addImage(chartImageData, "PNG", chartX, yPos, chartWidth, chartHeight, undefined, "FAST");
        
        yPos += chartHeight + 12;
        
        pdf.setFontSize(8);
        pdf.setTextColor(100, 100, 100);
        pdf.setFont("helvetica", "italic");
        pdf.text("Figure 1: Temperature Trend Chart", pageWidth / 2, yPos - 4, { align: "center" });
        pdf.setFontSize(7);
        pdf.setTextColor(80, 80, 80);
        pdf.text(`(Normal Range: ${minNormalTemp}°C - ${maxNormalTemp}°C)`, pageWidth / 2, yPos - 1, { align: "center" });
        
      } catch (err) {
        console.error("Error adding chart to PDF:", err);
      }
    }
    
    if (totalRecords > 0) {
      pdf.setFontSize(11);
      pdf.setTextColor(0, 0, 0);
      pdf.setFont("helvetica", "bold");
      yPos += 7;
      
      pdf.setFontSize(8);
      pdf.setTextColor(100, 100, 100);
      pdf.setFont("helvetica", "normal");
      yPos += 8;
      
      yPos = addTableHeader(yPos);
      
      const colWidths = [12, 70, 35, 40];
      let rowCount = 0;
      
      for (let i = 0; i < tableRows.length; i++) {
        const row = tableRows[i];
        const cells = row.querySelectorAll('.MuiTableCell-root, td');
        
        if (cells.length >= 4) {
          if (yPos + 7 > pageHeight - 25) {
            addFooter(yPos);
            yPos = addNewPage();
            pdf.setFontSize(10);
            pdf.setTextColor(0, 0, 0);
            pdf.setFont("helvetica", "bold");
            yPos += 7;
            pdf.setFontSize(8);
            yPos += 8;
            yPos = addTableHeader(yPos);
          }
          
          const recordNumber = rowCount + 1;
          const dateTime = cells[1]?.textContent?.trim() || '';
          let tempText = cells[2]?.textContent?.trim() || '0°C';
          let status = cells[3]?.textContent?.trim() || '';
          
          const tempMatch = tempText.match(/[\d.]+/);
          const temp = tempMatch ? parseFloat(tempMatch[0]) : 0;
          const isLow = temp < minNormalTemp;
          const isHigh = temp > maxNormalTemp;
          
          let currentX = 15;
          
          if (isLow) {
            pdf.setFillColor(255, 235, 238);
            pdf.rect(currentX, yPos - 3.5, colWidths.reduce((a, b) => a + b, 0), 6, "F");
            pdf.setTextColor(198, 40, 40);
          } else if (isHigh) {
            pdf.setFillColor(255, 243, 224);
            pdf.rect(currentX, yPos - 3.5, colWidths.reduce((a, b) => a + b, 0), 6, "F");
            pdf.setTextColor(230, 81, 0);
          } else {
            pdf.setTextColor(0, 0, 0);
          }
          
          pdf.text(recordNumber.toString(), currentX + 3, yPos);
          currentX += colWidths[0];
          
          const displayDate = dateTime.length > 45 ? dateTime.substring(0, 42) + "..." : dateTime;
          pdf.text(displayDate || '-', currentX + 2, yPos);
          currentX += colWidths[1];
          
          pdf.text(tempText, currentX + 3, yPos);
          currentX += colWidths[2];
          
          // Clean the status text
          const cleanStatus = cleanStatusText(status);
          pdf.text(cleanStatus.substring(0, 30), currentX + 2, yPos);
          
          let borderX = 15;
          pdf.setDrawColor(0, 0, 0);
          pdf.setLineWidth(0.2);
          
          colWidths.forEach((width) => {
            pdf.rect(borderX, yPos - 3.5, width, 6);
            borderX += width;
          });
          
          pdf.setTextColor(0, 0, 0);
          yPos += 6;
          rowCount++;
        }
      }
      
      console.log(`Added ${rowCount} rows to PDF`);
      addFooter(yPos);
    } else {
      console.warn("No records found");
      pdf.setFontSize(10);
      pdf.setTextColor(0, 0, 0);
      pdf.text("No data records found", 15, yPos + 20);
      addFooter(yPos);
    }
    
    pdf.save(filename);
    console.log("PDF saved successfully");
    
    const overlay = document.getElementById("pdf-loading-overlay");
    if (overlay) {
      document.body.removeChild(overlay);
    }
    
    return true;
  } catch (error) {
    console.error("Error generating PDF:", error);
    const overlay = document.getElementById("pdf-loading-overlay");
    if (overlay) {
      document.body.removeChild(overlay);
    }
    throw error;
  }
};

export const generateSimplifiedPDF = async (data, options = {}) => {
  const {
    filename = "Temperature_Data_Export.pdf",
    title = "Temperature Data Report",
    fromDate = "",
    toDate = "",
    statistics = {},
    warehouseName = "",
    organization = "",
    season = "",
    typeOfDryer = "",
    binNo = "",
    cycleNo = "",
    minNormalTemp = 38,
    maxNormalTemp = 42,
  } = options;

  return new Promise((resolve, reject) => {
    try {
      const pdf = new jsPDF({
        orientation: "landscape",
        unit: "mm",
        format: "a4",
      });

      const RECORDS_PER_PAGE = 25;
      const totalPages = Math.ceil(data.length / RECORDS_PER_PAGE);
      const pageWidth = pdf.internal.pageSize.getWidth();
      
      for (let pageNum = 0; pageNum < totalPages; pageNum++) {
        if (pageNum > 0) {
          pdf.addPage();
        }
        
        let yPos = 20;
        const startIdx = pageNum * RECORDS_PER_PAGE;
        const endIdx = Math.min(startIdx + RECORDS_PER_PAGE, data.length);
        const pageData = data.slice(startIdx, endIdx);
        
        pdf.setFontSize(18);
        pdf.setTextColor(33, 33, 33);
        pdf.setFont("helvetica", "bold");
        pdf.text("Temperature Data Report", pageWidth / 2, yPos, { align: "center" });
        yPos += 8;
        
        const filters = [];
        if (organization) filters.push(`Organization: ${organization}`);
        if (warehouseName) filters.push(`Warehouse: ${warehouseName}`);
        if (season) filters.push(`Season: ${season}`);
        if (typeOfDryer) filters.push(`Dryer Type: ${typeOfDryer}`);
        if (binNo) filters.push(`Bin No: ${binNo}`);
        if (cycleNo) filters.push(`Cycle No: ${cycleNo}`);
        
        if (filters.length > 0) {
          pdf.setFontSize(8);
          pdf.setTextColor(80, 80, 80);
          pdf.setFont("helvetica", "normal");
          pdf.text(filters.join(" | "), pageWidth / 2, yPos, { align: "center" });
          yPos += 6;
        }
        
        pdf.setFontSize(10);
        pdf.setTextColor(100, 100, 100);
        pdf.setFont("helvetica", "normal");
        
        if (fromDate && toDate) {
          pdf.text(`Period: ${fromDate} to ${toDate}`, pageWidth / 2, yPos, { align: "center" });
          yPos += 7;
        }
        
        pdf.text(`Page ${pageNum + 1} of ${totalPages}`, pageWidth / 2, yPos, { align: "center" });
        yPos += 7;
        pdf.text(`Generated: ${new Date().toLocaleString()}`, pageWidth / 2, yPos, { align: "center" });
        yPos += 12;

        if (pageNum === 0 && statistics && Object.keys(statistics).length > 0) {
          pdf.setFontSize(11);
          pdf.setTextColor(0, 0, 0);
          pdf.setFont("helvetica", "bold");
          pdf.text("Summary Statistics", 15, yPos);
          yPos += 8;
          
          pdf.setFontSize(8);
          pdf.setTextColor(80, 80, 80);
          pdf.setFont("helvetica", "normal");
          
          const statsData = [
            { label: "Total Records", value: statistics.totalRecords || 0 },
            { label: "Average Temp", value: `${statistics.avgTemperature || 0}°C` },
            { label: "Max Temp", value: `${statistics.maxTemperature || 0}°C` },
            { label: "Min Temp", value: `${statistics.minTemperature || 0}°C` },
            { label: "Abnormal", value: statistics.abnormalCount || 0 },
            { label: `Low (<${minNormalTemp}°C)`, value: statistics.lowCount || 0 },
            { label: `High (>${maxNormalTemp}°C)`, value: statistics.highCount || 0 },
          ];
          
          let xPos = 15;
          statsData.forEach((stat, idx) => {
            pdf.text(`${stat.label}: ${stat.value}`, xPos, yPos);
            xPos += 55;
            if ((idx + 1) % 4 === 0) {
              xPos = 15;
              yPos += 5;
            }
          });
          yPos += 10;
        }
        
        pdf.setFontSize(8);
        pdf.setTextColor(100, 100, 100);
        pdf.text(`Records ${startIdx + 1} to ${endIdx} of ${data.length}`, 15, yPos);
        yPos += 8;
        
        const headers = ["#", "Date & Time", "Temp (°C)", "Status"];
        const colWidths = [12, 70, 30, 40];
        
        pdf.setFillColor(63, 81, 181);
        pdf.setTextColor(255, 255, 255);
        pdf.setFontSize(8);
        pdf.setFont("helvetica", "bold");
        
        let xPos = 15;
        headers.forEach((header, i) => {
          pdf.rect(xPos, yPos - 5, colWidths[i], 7, "F");
          pdf.text(header, xPos + 2, yPos);
          xPos += colWidths[i];
        });
        
        pdf.setTextColor(0, 0, 0);
        pdf.setFont("helvetica", "normal");
        yPos += 3;
        
        for (let i = 0; i < pageData.length; i++) {
          const item = pageData[i];
          const recordNumber = startIdx + i + 1;
          const temp = parseFloat(item.upfronttemp);
          const isLow = temp < minNormalTemp;
          const isHigh = temp > maxNormalTemp;
          const dateObj = new Date(item.datetime);
          
          if (yPos + 6 > 190) {
            pdf.addPage();
            yPos = 20;
            
            pdf.setFontSize(11);
            pdf.setTextColor(0, 0, 0);
            pdf.setFont("helvetica", "bold");
            yPos += 8;
            
            pdf.setFillColor(63, 81, 181);
            pdf.setTextColor(255, 255, 255);
            pdf.setFontSize(8);
            pdf.setFont("helvetica", "bold");
            
            let headerX = 15;
            headers.forEach((header, idx) => {
              pdf.rect(headerX, yPos - 5, colWidths[idx], 7, "F");
              pdf.text(header, headerX + 2, yPos);
              headerX += colWidths[idx];
            });
            
            pdf.setTextColor(0, 0, 0);
            pdf.setFont("helvetica", "normal");
            yPos += 3;
          }
          
          let currentX = 15;
          
          if (isLow) {
            pdf.setTextColor(198, 40, 40);
          } else if (isHigh) {
            pdf.setTextColor(230, 81, 0);
          } else {
            pdf.setTextColor(0, 0, 0);
          }
          
          pdf.rect(currentX, yPos - 4, colWidths[0], 6);
          pdf.text(recordNumber.toString(), currentX + 3, yPos);
          currentX += colWidths[0];
          
          pdf.rect(currentX, yPos - 4, colWidths[1], 6);
          const dateTimeStr = `${dateObj.toLocaleDateString('en-GB')} ${dateObj.toLocaleTimeString('en-GB', { hour12: false })}`;
          pdf.text(dateTimeStr.length > 35 ? dateTimeStr.substring(0, 32) + "..." : dateTimeStr, currentX + 2, yPos);
          currentX += colWidths[1];
          
          pdf.rect(currentX, yPos - 4, colWidths[2], 6);
          pdf.text(`${temp.toFixed(2)}°C`, currentX + 3, yPos);
          currentX += colWidths[2];
          
          pdf.rect(currentX, yPos - 4, colWidths[3], 6);
          let status = "Normal";
          if (isLow) {
            status = `Below ${minNormalTemp}°C`;
          } else if (isHigh) {
            status = `Above ${maxNormalTemp}°C`;
          }
          pdf.text(status, currentX + 2, yPos);
          
          pdf.setTextColor(0, 0, 0);
          yPos += 6;
        }
        
        if (yPos < 195) {
          yPos = 195;
        }
        pdf.setFontSize(7);
        pdf.setTextColor(120, 120, 120);
        pdf.text(`Normal Range: ${minNormalTemp}°C - ${maxNormalTemp}°C`, 15, yPos);
        pdf.text(`Page ${pageNum + 1} of ${totalPages}`, pageWidth / 2, yPos, { align: "center" });
      }
      
      pdf.save(filename);
      resolve(true);
      
    } catch (error) {
      console.error("PDF generation error:", error);
      reject(error);
    }
  });
};

export const generateChartOnlyPDF = async (chartElement, options = {}) => {
  const {
    filename = "Temperature_Chart_Only.pdf",
    title = "Temperature Trend Chart",
    fromDate = "",
    toDate = "",
    organization = "",
    season = "",
    typeOfDryer = "",
    binNo = "",
    cycleNo = "",
    warehouseName = "",
    minNormalTemp = 38,
    maxNormalTemp = 42,
  } = options;

  try {
    let chartContainer = null;
    
    if (typeof chartElement === 'string') {
      chartContainer = document.getElementById(chartElement);
    } else if (chartElement && chartElement.nodeType) {
      chartContainer = chartElement;
    } else {
      chartContainer = document.querySelector('.MuiPaper-root .recharts-responsive-container')?.closest('.MuiPaper-root');
      if (!chartContainer) {
        chartContainer = document.querySelector('.recharts-wrapper')?.closest('.MuiPaper-root');
      }
    }
    
    if (!chartContainer) {
      throw new Error("Chart container not found");
    }

    const loadingOverlay = document.createElement("div");
    loadingOverlay.id = "pdf-loading-overlay";
    loadingOverlay.style.cssText = `
      position: fixed;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
      background: rgba(0,0,0,0.7);
      display: flex;
      justify-content: center;
      align-items: center;
      z-index: 9999;
      color: white;
      font-size: 24px;
      font-family: Arial, sans-serif;
      z-index: 10000;
    `;
    loadingOverlay.innerHTML = `<div>📊 Generating Chart PDF...</div>`;
    document.body.appendChild(loadingOverlay);

    const clonedChart = chartContainer.cloneNode(true);
    clonedChart.style.position = 'absolute';
    clonedChart.style.top = '-9999px';
    clonedChart.style.left = '-9999px';
    clonedChart.style.width = '1400px';
    clonedChart.style.padding = '5px';
    clonedChart.style.backgroundColor = '#ffffff';
    clonedChart.style.border = '1px solid #d0d0d0';
    clonedChart.style.borderRadius = '12px';
    
    document.body.appendChild(clonedChart);
    
    await new Promise(resolve => setTimeout(resolve, 500));
    
    const canvas = await html2canvas(clonedChart, {
      scale: 4,
      backgroundColor: "#ffffff",
      logging: false,
      useCORS: true,
      allowTaint: false,
      windowWidth: 1400,
    });
    
    document.body.removeChild(clonedChart);
    
    const imgData = canvas.toDataURL("image/png");
    const pdf = new jsPDF({
      orientation: "landscape",
      unit: "mm",
      format: "a4",
    });
    
    const padding = 10;
    const pageWidth = pdf.internal.pageSize.getWidth();
    const pageHeight = pdf.internal.pageSize.getHeight();
    const imgWidth = pageWidth - (padding * 2);
    const imgHeight = (canvas.height * imgWidth) / canvas.width;
    
    pdf.setFontSize(20);
    pdf.setTextColor(33, 33, 33);
    pdf.setFont("helvetica", "bold");
    pdf.text(title, pageWidth / 2, 20, { align: "center" });
    
    pdf.setFontSize(11);
    pdf.setTextColor(100, 100, 100);
    pdf.setFont("helvetica", "normal");
    
    let subtitleY = 32;
    
    const filters = [];
    if (organization) filters.push(`Organization: ${organization}`);
    if (warehouseName) filters.push(`Warehouse: ${warehouseName}`);
    if (season) filters.push(`Season: ${season}`);
    if (typeOfDryer) filters.push(`Dryer Type: ${typeOfDryer}`);
    if (binNo) filters.push(`Bin No: ${binNo}`);
    if (cycleNo) filters.push(`Cycle No: ${cycleNo}`);
    
    if (filters.length > 0) {
      pdf.setFontSize(8);
      pdf.text(filters.join(" | "), pageWidth / 2, subtitleY, { align: "center" });
      subtitleY += 6;
    }
    
    if (fromDate && toDate) {
      pdf.text(`Period: ${fromDate} to ${toDate}`, pageWidth / 2, subtitleY, { align: "center" });
      subtitleY += 8;
    }
    
    pdf.setFontSize(9);
    pdf.text(`Generated on: ${new Date().toLocaleString()}`, pageWidth / 2, subtitleY, { align: "center" });
    
    let chartY = subtitleY + 15;
    pdf.addImage(imgData, "PNG", padding, chartY, imgWidth, imgHeight);
    
    const footerY = chartY + imgHeight + 10;
    if (footerY < pageHeight - 15) {
      pdf.setFontSize(9);
      pdf.setTextColor(80, 80, 80);
      pdf.text(`Normal Range: ${minNormalTemp}°C - ${maxNormalTemp}°C | Green zone indicates normal temperature range`, pageWidth / 2, footerY, { align: "center" });
      pdf.text(`Temperature Monitoring System`, pageWidth / 2, footerY + 6, { align: "center" });
      pdf.text(`Page 1 of 1`, pageWidth / 2, footerY + 12, { align: "center" });
    }
    
    pdf.save(filename);
    
    const overlay = document.getElementById("pdf-loading-overlay");
    if (overlay) {
      document.body.removeChild(overlay);
    }
    
    return true;
  } catch (error) {
    console.error("Chart PDF generation error:", error);
    const overlay = document.getElementById("pdf-loading-overlay");
    if (overlay) {
      document.body.removeChild(overlay);
    }
    throw error;
  }
};

export const generateExcelReport = async (data, statistics, options = {}) => {
  const {
    filename = 'Temperature_Report.xlsx',
    fromDate = '',
    toDate = '',
    chartData = [],
    warehouseName = '',
    organization = '',
    season = '',
    typeOfDryer = '',
    binNo = '',
    cycleNo = '',
    minNormalTemp = 38,
    maxNormalTemp = 42,
  } = options;

  try {
    const now = new Date();
    const formattedDate = now.toLocaleDateString('en-GB');
    const formattedTime = now.toLocaleTimeString('en-GB', { hour12: false });
    
    const workbook = XLSX.utils.book_new();
    
    const mainSheetData = [];
    
    mainSheetData.push(["TEMPERATURE TIMELINE REPORT"]);
    mainSheetData.push([]);
    
    if (organization && organization.trim()) mainSheetData.push(["Organization:", organization]);
    if (warehouseName && warehouseName.trim()) mainSheetData.push(["Warehouse:", warehouseName]);
    if (season && season.trim()) mainSheetData.push(["Season:", season]);
    if (typeOfDryer && typeOfDryer.trim()) mainSheetData.push(["Dryer Type:", typeOfDryer]);
    if (binNo && binNo.trim()) mainSheetData.push(["Bin No:", binNo]);
    if (cycleNo && cycleNo.trim()) mainSheetData.push(["Cycle No:", cycleNo]);
    mainSheetData.push([]);
    
    mainSheetData.push([`Period: ${fromDate} to ${toDate}`]);
    mainSheetData.push([`Generated: ${formattedDate}, ${formattedTime}`]);
    mainSheetData.push([]);
    
    mainSheetData.push(["SUMMARY STATISTICS"]);
    mainSheetData.push([]);
    mainSheetData.push(["Metric", "Value"]);
    mainSheetData.push(["Total Records", statistics.totalRecords || 0]);
    mainSheetData.push(["Average Temperature", `${statistics.avgTemperature || 0}°C`]);
    mainSheetData.push(["Maximum Temperature", `${statistics.maxTemperature || 0}°C`]);
    mainSheetData.push(["Minimum Temperature", `${statistics.minTemperature || 0}°C`]);
    mainSheetData.push(["Abnormal Readings", statistics.abnormalCount || 0]);
    mainSheetData.push([`Low Readings (<${minNormalTemp}°C)`, statistics.lowCount || 0]);
    mainSheetData.push([`High Readings (>${maxNormalTemp}°C)`, statistics.highCount || 0]);
    
    const totalRecords = statistics.totalRecords || 0;
    const abnormalCount = statistics.abnormalCount || 0;
    const complianceRate = totalRecords > 0 ? ((totalRecords - abnormalCount) / totalRecords * 100).toFixed(1) : '0';
    mainSheetData.push(["Compliance Rate", `${complianceRate}%`]);
    mainSheetData.push([]);
    mainSheetData.push(["Normal Range", `${minNormalTemp}°C - ${maxNormalTemp}°C`]);
    mainSheetData.push([]);
    mainSheetData.push([]);
    
    mainSheetData.push(["DETAILED TEMPERATURE DATA"]);
    mainSheetData.push([]);
    mainSheetData.push(["Sr. No.", "Date", "Time", "Temperature (°C)", "Status"]);
    
    if (data && data.length > 0) {
      data.forEach((item, index) => {
        const temperature = parseFloat(item.upfronttemp);
        const isLow = temperature < minNormalTemp;
        const isHigh = temperature > maxNormalTemp;
        let status = "Normal";
        if (isLow) status = `Below ${minNormalTemp}°C`;
        if (isHigh) status = `Above ${maxNormalTemp}°C`;
        
        const dateObj = new Date(item.datetime);
        const dateStr = dateObj.toLocaleDateString('en-GB');
        const timeStr = dateObj.toLocaleTimeString('en-GB', { hour12: false });
        
        mainSheetData.push([
          index + 1,
          dateStr,
          timeStr,
          temperature.toFixed(2),
          status
        ]);
      });
    } else {
      mainSheetData.push(["No data available for the selected date range", "", "", "", ""]);
    }
    
    mainSheetData.push([]);
    mainSheetData.push([]);
    
    mainSheetData.push(["DAILY TEMPERATURE STATISTICS"]);
    mainSheetData.push([]);
    mainSheetData.push(["Date", "Total Records", "Average Temp", "Max Temp", "Min Temp", "Abnormal Count", "Compliance Rate"]);
    
    const dateGroups = {};
    if (data && data.length > 0) {
      data.forEach(item => {
        const dateObj = new Date(item.datetime);
        const dateKey = dateObj.toLocaleDateString('en-GB');
        if (!dateGroups[dateKey]) {
          dateGroups[dateKey] = [];
        }
        dateGroups[dateKey].push(parseFloat(item.upfronttemp));
      });
      
      Object.keys(dateGroups).sort().forEach(date => {
        const temps = dateGroups[date];
        const avgTemp = (temps.reduce((a, b) => a + b, 0) / temps.length).toFixed(2);
        const maxTemp = Math.max(...temps);
        const minTemp = Math.min(...temps);
        const abnormalCount = temps.filter(t => t < minNormalTemp || t > maxNormalTemp).length;
        const complianceRate = ((temps.length - abnormalCount) / temps.length * 100).toFixed(1);
        
        mainSheetData.push([
          date,
          temps.length,
          `${avgTemp}°C`,
          `${maxTemp}°C`,
          `${minTemp}°C`,
          abnormalCount,
          `${complianceRate}%`
        ]);
      });
    }
    
    const mainSheet = XLSX.utils.aoa_to_sheet(mainSheetData);
    mainSheet['!cols'] = [
      {wch: 25},
      {wch: 20},
      {wch: 20},
      {wch: 20},
      {wch: 20},
      {wch: 20},
      {wch: 20}
    ];
    XLSX.utils.book_append_sheet(workbook, mainSheet, "Temperature Report");
    
    XLSX.writeFile(workbook, filename);
    return true;
    
  } catch (error) {
    console.error("Excel generation error:", error);
    throw error;
  }
};